import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import {
  FaUpload,
  FaCheckCircle,
  FaTimesCircle,
  FaFileExcel,
  FaSpinner,
  FaTimes,
  FaSave,
} from "react-icons/fa";

const AddClientModal = ({ onClose, onSubmit }) => {
  const [salesExecutives, setSalesExecutives] = useState([]);
  const [clientDetails, setClientDetails] = useState({
    firmName: "",
    contactPerson: "",
    mobileNo: "",
    waGroupId: "",
    email: "",
    assignedSalesExecutive: "",
  });
  const [excelFile, setExcelFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({
    total: 0,
    success: 0,
    failure: 0,
  });

  useEffect(() => {
    const fetchSalesExecutives = async () => {
      try {
        const response = await fetch(
          buildApiUrl("/api/users/sales-executives"),
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setSalesExecutives(data.salesExecutives);
        } else {
          toast.error("Failed to fetch sales executives.");
        }
      } catch (error) {
        toast.error("Error fetching sales executives.");
        console.error("Error fetching sales executives:", error);
      }
    };
    fetchSalesExecutives();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClientDetails({
      ...clientDetails,
      [name]: value,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setExcelFile(file);
    }
  };

  const handleExcelUpload = () => {
    if (!excelFile) {
      toast.error("Please select an Excel file to upload.");
      return;
    }

    setLoading(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length > 0) {
        setUploadStatus({
          total: jsonData.length,
          success: 0,
          failure: 0,
        });

        const uploadPromises = jsonData.map((client) => {
          return fetch(buildApiUrl("/api/clients"), {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(client),
          }).then((response) => {
            if (response.ok) {
              setUploadStatus((prevStatus) => ({
                ...prevStatus,
                success: prevStatus.success + 1,
              }));
            } else {
              setUploadStatus((prevStatus) => ({
                ...prevStatus,
                failure: prevStatus.failure + 1,
              }));
            }
          });
        });

        Promise.all(uploadPromises)
          .then(() => {
            if (uploadStatus.success === jsonData.length) {
              toast.success("All Clients Added Successfully!");
              onSubmit();
            } else {
              toast.error(`${uploadStatus.failure} clients failed to upload.`);
            }
          })
          .catch(() => {
            toast.error("An error occurred while uploading clients.");
          })
          .finally(() => {
            setLoading(false);
            onClose();
          });
      } else {
        setLoading(false);
        toast.error("No valid data found in the uploaded Excel file.");
      }
    };
    reader.readAsArrayBuffer(excelFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    const token = localStorage.getItem("token");

    try {
      const response = await fetch(buildApiUrl("/api/clients"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(clientDetails),
      });

      if (response.ok) {
        onSubmit();
        onClose();
        toast.success("Client Added successfully!");
      } else {
        toast.error("Failed to Add Client.");
      }
    } catch (error) {
      console.error("Error adding client:", error);
      toast.error("Error adding client.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDownloadSample = () => {
    const sampleData = [
      {
        firmName: "Sample Travel",
        contactPerson: "John Doe",
        mobileNo: "9876543210",
        waGroupId: "1234567890",
        email: "john.doe@example.com",
        assignedSalesExecutive: "Amrit",
      },
      {
        firmName: "Sample Tours",
        contactPerson: "Jane Smith",
        mobileNo: "1234567890",
        waGroupId: "0987654321",
        email: "jane.smith@example.com",
        assignedSalesExecutive: "Jane smith",
      },
    ];
    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample Clients");
    XLSX.writeFile(workbook, "Sample_Clients.xlsx");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-3">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[95vh] overflow-y-auto relative transform transition-all duration-300 scale-100">
        {/* Loading Overlay */}
        {(loading || formLoading) && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-2xl z-20">
            <div className="flex flex-col items-center gap-2">
              <FaSpinner className="w-8 h-8 text-blue-600 animate-spin" />
              <p className="text-xs font-semibold text-blue-600">
                {loading ? "Processing Excel Upload..." : "Adding Client..."}
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex justify-between items-center px-3 py-2 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-800 ">Add New Client</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            disabled={loading || formLoading}
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-3 py-2 space-y-3">
          <div className="grid grid-cols-1 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Firm Name
              </label>
              <input
                type="text"
                name="firmName"
                value={clientDetails.firmName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 outline-none focus:ring-blue-500 focus:border-blue-500 text-xs transition-all duration-200 disabled:bg-gray-100"
                disabled={formLoading}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Contact Person
              </label>
              <input
                type="text"
                name="contactPerson"
                value={clientDetails.contactPerson}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 outline-none focus:ring-blue-500 focus:border-blue-500 text-xs transition-all duration-200 disabled:bg-gray-100"
                disabled={formLoading}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Mobile Number
              </label>
              <input
                type="text"
                name="mobileNo"
                value={clientDetails.mobileNo}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 outline-none focus:ring-blue-500 focus:border-blue-500 text-xs transition-all duration-200 disabled:bg-gray-100"
                disabled={formLoading}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                WhatsApp Group ID
              </label>
              <input
                type="text"
                name="waGroupId"
                value={clientDetails.waGroupId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 outline-none focus:ring-blue-500 focus:border-blue-500 text-xs transition-all duration-200 disabled:bg-gray-100"
                disabled={formLoading}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Email
              </label>
              <input
                type="text"
                name="email"
                value={clientDetails.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 outline-none focus:ring-blue-500 focus:border-blue-500 text-xs transition-all duration-200 disabled:bg-gray-100"
                disabled={formLoading}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Assigned Sales Executive
              </label>
              <select
                name="assignedSalesExecutive"
                value={clientDetails.assignedSalesExecutive}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 outline-none focus:ring-blue-500 focus:border-blue-500 text-xs transition-all duration-200 disabled:bg-gray-100"
                disabled={formLoading}
              >
                <option value="">Select Sales Executive</option>
                {salesExecutives.map((executive) => (
                  <option key={executive.id} value={executive.id}>
                    {executive.name} - {executive.email}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-3">
            <button
              type="submit"
              className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs transition-all duration-200 disabled:bg-blue-300"
              disabled={formLoading || loading}
            >
              <FaSave className="w-3 h-3" />
              {formLoading ? "Adding..." : "Add Client"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs transition-all duration-200 disabled:bg-red-300"
              disabled={formLoading || loading}
            >
              <FaTimes className="w-3 h-3" />
              Cancel
            </button>
          </div>

          <div className="mt-3">
            <h3 className="text-xs font-semibold text-gray-700 mb-2">
              Upload Clients from Excel
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={handleFileChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs transition-all duration-200 disabled:bg-gray-100"
                  disabled={loading || formLoading}
                />
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleExcelUpload}
                  className="flex items-center gap-1 w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs transition-all duration-200 disabled:bg-green-300"
                  disabled={loading || formLoading}
                >
                  <FaUpload className="w-3 h-3" />
                  {loading ? "Uploading..." : "Upload Clients"}
                </button>
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleDownloadSample}
                  className="flex items-center gap-1 w-full px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-xs transition-all duration-200 disabled:bg-yellow-300"
                  disabled={loading || formLoading}
                >
                  <FaFileExcel className="w-3 h-3" />
                  Sample XLSX
                </button>
              </div>
            </div>

            {loading && (
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="bg-blue-600 h-1.5 transition-all duration-300 ease-in-out"
                    style={{
                      width: `${
                        (uploadStatus.success / uploadStatus.total) * 100
                      }%`,
                    }}
                  ></div>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
                  <FaCheckCircle className="w-3 h-3 text-green-500" />
                  {uploadStatus.success}/{uploadStatus.total} Uploaded
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
      <ToastContainer />
    </div>
  );
};

export default AddClientModal;
